/*
  Resource Experience Reducer + action definitions
*/
import produce from 'immer-legacy';
import { arrayMove } from 'react-sortable-hoc';

import { LOGOUT } from 'data_system/token_auth/actionTypes';

import {
  experienceSelected,
  showAvailabilityModal,
  cancelledCreateSchedule,
  dateSelected,
  timeSelected,
  createScheduleSelected,
  saveSchedule,
  saveScheduleSucceeded,
  saveScheduleFailed,
  fetchExperienceAvailabilityStarted,
  fetchExperienceAvailabilitySucceeded,
  fetchExperienceAvailabilityFailed,
} from 'data_system/resource_portal/actions/experienceActions';

import {
  CREATE_EXPERIENCE_MEETING_SUCCESS,
  CREATE_EXPERIENCE_MEETING_REQUEST,
  UPDATE_EXPERIENCE_MEETING_SUCCESS,
  UPDATE_EXPERIENCE_MEETING_REQUEST,
} from '../actions/experienceActionTypes';

export const steps = [
  {
    title: 'About',
    slug: 'about',
    description: 'Class Description',
    selected: false,
    completed: false,
  },
  {
    title: 'Details',
    slug: 'details',
    description: 'Location, Length, Price',
    selected: false,
    completed: false,
  },
  {
    title: 'Photos',
    slug: 'photos',
    description: 'Project Photos',
    selected: false,
    completed: false,
  },
];

// Notification after save, etc
const SHOW_NOTIFICATION = 'SHOW_NOTIFICATION';

// Experiences
const FETCH_EXPERIENCE_REQUEST = 'FETCH_EXPERIENCE_REQUEST';
const FETCH_EXPERIENCE_SUCCESS = 'FETCH_EXPERIENCE_SUCCESS';
const CREATE_EXPERIENCE_REQUEST = 'CREATE_EXPERIENCE_REQUEST';
const CREATE_EXPERIENCE_SUCCESS = 'CREATE_EXPERIENCE_SUCCESS';
const FETCH_EXPERIENCES_REQUEST = 'FETCH_EXPERIENCES_REQUEST';
const FETCH_EXPERIENCES_SUCCESS = 'FETCH_EXPERIENCES_SUCCESS';
const UPDATE_EXPERIENCE_REQUEST = 'UPDATE_EXPERIENCE_REQUEST';
const UPDATE_EXPERIENCE_SUCCESS = 'UPDATE_EXPERIENCE_SUCCESS';
const UPDATE_TOUR_STEP_FORM = 'UPDATE_TOUR_STEP_FORM';
const SET_EXPERIENCE_LENGTH = 'SET_EXPERIENCE_LENGTH';

// Editor steps
const STEP_SELECTED = 'STEP_SELECTED';

// Modals
const TOGGLE_CHANGES_MODAL = 'TOGGLE_CHANGES_MODAL';
const TOGGLE_NEW_EXPERIENCE_MODAL = 'TOGGLE_NEW_EXPERIENCE_MODAL';
const MOBILE_TABS_MENU_TOGGLED = 'MOBILE_TABS_MENU_TOGGLED';

// Photos
const CREATE_PHOTO_REQUEST = 'CREATE_PHOTO_REQUEST';
const CREATE_PHOTO_SUCCESS = 'CREATE_PHOTO_SUCCESS';
const CREATE_PHOTO_FAILURE = 'CREATE_PHOTO_FAILURE';
const REMOVE_PHOTO_REQUEST = 'REMOVE_PHOTO_REQUEST';
const REMOVE_PHOTO_SUCCESS = 'REMOVE_PHOTO_SUCCESS';
const MOVE_PHOTO_REQUEST = 'MOVE_PHOTO_REQUEST';

// Initial state to initialize reducer
const initialState = {
  // Server-rendered state loaded via props
  brand: {},
  user: null,
  //
  resource: {},
  // Experiences list object. Key name is dictated by the resource "type" per jsonapi
  tour: {},
  tourSchedule: {},
  // Experience Editor
  noticeMessage: '',
  previewable: false,
  mobileTabsMenuToggled: true,
  showChangesModal: false,
  changesModalRedirectUrl: '',
  continueToStep: '',
  images: [],
  imagesUploading: 0,
  onlineMeeting: {},
  onlineMeetingVendors: [],
  steps,
  tourStepForm: null,
  allStepsCompleted: false,
  experience: null,
  experienceStepForm: null,
  experienceFetching: false,
  experienceUpdating: false,
  showNewExperienceModal: false,
  // Availability and length
  selectedAvailability: false,
  experienceLength: null,
  experienceAvailabilityDirty: false,
  isOnline: false,
};

const experienceReducer = produce((state = initialState, action) => {
  switch (action.type) {
    case LOGOUT:
      return initialState;

    // Shows notification at top of editor
    case SHOW_NOTIFICATION: {
      state.noticeMessage = action.noticeMessage;
      break;
    }

    // Experiences data
    case FETCH_EXPERIENCE_REQUEST:
      state.experienceFetching = true;
      break;

    case FETCH_EXPERIENCES_REQUEST:
      state.experiencesFetching = true;
      break;
    case FETCH_EXPERIENCE_SUCCESS:
      state.experience = action.experience;
      state.experienceFetching = false;
      state.onlineMeeting = action.onlineMeeting;
      state.onlineMeetingVendors = action.onlineMeetingVendors;
      state.images = action.experience.assets.data.map((a) => a.attributes);
      break;
    case FETCH_EXPERIENCES_SUCCESS: {
      for (const [_id, experience] of Object.entries(action.tour)) {
        experience.attributes.availabilityDirty = true;
      }
      state.tour = state.tour || {};
      state.tour = {
        ...state.tour,
        ...action.tour,
      };

      state.experiencesFetching = false;
      state.experienceLength = Object.keys(state.tour).length;
      break;
    }

    case CREATE_EXPERIENCE_REQUEST: {
      state.experienceUpdating = true;
      break;
    }

    case CREATE_EXPERIENCE_SUCCESS: {
      state.experienceUpdating = false;
      state.showNewExperienceModal = false;
      break;
    }

    case UPDATE_EXPERIENCE_REQUEST: {
      state.experienceUpdating = true;
      break;
    }

    case UPDATE_EXPERIENCE_SUCCESS: {
      state.experienceUpdating = false;
      state.experience = action.experience;
      state.images = action.assets || [];
      state.experienceErrors = action.experienceErrors;
      state.tourStepForm = null;
      break;
    }

    case CREATE_EXPERIENCE_MEETING_REQUEST: {
      state.experienceUpdating = true;
      break;
    }

    case CREATE_EXPERIENCE_MEETING_SUCCESS: {
      state.experienceUpdating = false;
      state.onlineMeeting = action.onlineMeeting;
      break;
    }

    case UPDATE_EXPERIENCE_MEETING_REQUEST: {
      state.experienceUpdating = true;
      break;
    }

    case UPDATE_EXPERIENCE_MEETING_SUCCESS: {
      state.experienceUpdating = false;
      state.onlineMeeting = action.onlineMeeting;
      break;
    }

    case UPDATE_TOUR_STEP_FORM: {
      state.tourStepForm = action.tourStepForm;
      break;
    }
    // =====

    // === PHOTOS
    case CREATE_PHOTO_REQUEST: {
      state.imagesUploading += 1;
      break;
    }

    case CREATE_PHOTO_SUCCESS: {
      state.images.push(action.asset);
      state.imagesUploading -= 1;
      break;
    }

    case CREATE_PHOTO_FAILURE: {
      state.imagesUploading -= 1;
      break;
    }

    case MOVE_PHOTO_REQUEST: {
      state.images = arrayMove(
        state.images,
        action.attrs.oldPosition,
        action.attrs.position - 1
      );
      break;
    }

    case REMOVE_PHOTO_REQUEST: {
      state.images = state.images.map((image) => {
        if (image.id === action.attrs.assetId) {
          image.removing = true;
          return image;
        }
        return image;
      });
      break;
    }

    case REMOVE_PHOTO_SUCCESS: {
      state.images = state.images.filter(
        (image) => image.id !== action.deletedImage.id
      );
      // setPreviewable(state);
      break;
    }
    // END PHOTOS

    // AVAILABILITY RULES ===
    case SET_EXPERIENCE_LENGTH: {
      state.experienceLength = action.length || 0.05;
      state.experienceAvailabilityDirty = true;
      break;
    }

    // New experience
    case TOGGLE_NEW_EXPERIENCE_MODAL: {
      state.showNewExperienceModal = action.visible;
      break;
    }

    // Form Changes
    case TOGGLE_CHANGES_MODAL: {
      state.showChangesModal = action.visible;
      state.changesModalRedirectUrl = action.redirectTo;
      break;
    }

    // Mobile menu
    case MOBILE_TABS_MENU_TOGGLED: {
      state.mobileTabsMenuToggled = !state.mobileTabsMenuToggled;
      break;
    }

    // Editor steps
    case STEP_SELECTED: {
      // Steps selected state - set on for clicked | off all others
      state.steps = state.steps.map((step) =>
        step.slug === action.step
          ? { ...step, selected: true }
          : { ...step, selected: false }
      );
      state.mobileTabsMenuToggled = false;
      // setPreviewable(state);
      break;
    }

    case experienceSelected.type: {
      state.selectedExperienceId = state.payload.experienceId;
      break;
    }

    case fetchExperienceAvailabilityStarted.type: {
      state.experienceAvailabilityLoading = true;
      break;
    }

    case fetchExperienceAvailabilitySucceeded.type: {
      state.experienceAvailabilityLoading = false;
      for (const [key, value] of Object.entries(action.payload)) {
        state[key] = value;
      }
      break;
    }

    case createScheduleSelected.type: {
      state.selectedExperienceId = action.payload;
      break;
    }

    case fetchExperienceAvailabilityFailed.type: {
      state.experienceAvailabilityLoading = false;
      break;
    }

    case showAvailabilityModal.type: {
      state.availabilityModalVisible = true;
      break;
    }

    case cancelledCreateSchedule.type: {
      state.availabilityModalVisible = false;
      state.selectedDate = null;
      state.selectedTime = null;
      break;
    }

    case dateSelected.type: {
      state.selectedDate = action.payload;
      break;
    }

    case timeSelected.type: {
      state.selectedTime = action.payload;
      break;
    }

    case saveSchedule.type: {
      state.savingSchedule = true;
      break;
    }

    case saveScheduleSucceeded.type: {
      state.savingSchedule = false;
      state.availabilityModalVisible = false;
      state.selectedDate = null;
      state.selectedTime = null;
      break;
    }

    case saveScheduleFailed.type: {
      state.savingSchedule = false;
      break;
    }

    default:
      break;
  }
  return state;
});

export default experienceReducer;
